/**
 * Copyright (c) 2018-present clchart Contributors.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

// 以下是 ClChartVLine 的实体定义  --- 画线类

// 竖向排列的图形
// 实际上就是获取某种类型数据，然后横坐标 0 开始排序，永远是数值型，但显示出什么要到对应的数组中找
// 纵坐标根据数据类型，计算最大最小值，然后根据画线类型画出数据线，所有的不同全部在ClChart中处理

import {
    _fillRect,
    _drawRect,
    _setLineWidth,
    _getTxtWidth,
    _drawHline,
    _drawBegin,
    _drawEnd
} from './cl.draw'
import {
    initCommonInfo,
    checkLayout,
    getLineColor
} from './cl.chart.init'
import {
    CHART_LAYOUT
} from '../cl.chart.res'
import {
    updateJsonOfDeep,
    copyJsonOfDeep,
    offsetRect,
    resumeRect,
    setJsonOfDeep,
    isEmptyArray,
    formatInfo,
    inRangeX,
    inRangeY,
    getSize,
} from '../cl.utils'

import ClChartLine from './cl.chart.line'

import { getValue } from '../datas/cl.data'
/**
 * Class representing ClChartVLine
 * @export
 * @class ClChartVLine
 */
export default class ClChartVLine extends ClChartLine {
    /**
     * init line
     * @param {Object} cfg line's config
     * @param {any} callback initialized callback
     * @memberof ClChartLine
     */
    init(cfg, callback) {
        this.callback = callback
        this.rectMain = cfg.rectMain || {
            left: 0,
            top: 0,
            width: 400,
            height: 200
        }

        this.layout = updateJsonOfDeep(cfg.layout, CHART_LAYOUT)
        this.config = copyJsonOfDeep(cfg.config)
        // 这里直接赋值是因为外部已经设置好了配置才会开始初始化
        this.buttons = cfg.config.buttons || []

        this.scroll = {
            display: 'none'
        }
        this.axisXdata = {}

        this.childCharts = {}
        // 下面对配置做一定的校验
        this.checkConfig()
        // 再做一些初始化运算，下面的运算范围是初始化设置后基本不变的数据
        this.setPublicRect()
        // 初始化子窗口
        this.childDraws = {}
        this.setChildDraw()
        this.childLines = {}
        this.setChildLines()
        // 初始化按钮
        this.createButtons()

    }
    changeAxisY(axisYdata) {
        if (axisYdata.Yhead !== this.rectChart.top) {
            this.rectChart.top = axisYdata.Yhead
            const oRect = resumeRect(this.rectChart, this.layout.offset)
            this.rectGridLine.top = oRect.top
            this.rectAxisYLeft.top = oRect.top
            this.rectAxisYRight.top = oRect.top
        }
        const height = axisYdata.Yfoot - axisYdata.Yhead
        if (height !== this.rectChart.height) {
            this.rectChart.height = height
            const oRect = resumeRect(this.rectChart, this.layout.offset)
            this.rectGridLine.height = oRect.height
            this.rectAxisYLeft.height = oRect.height
            this.rectAxisYRight.height = oRect.height
        }
        setJsonOfDeep(this.axisYdata, axisYdata)
        // this.axisYdata.minIndex = -1
        // this.axisYdata.maxIndex = -1
        // this.axisYdata.moveIndex = -1
        // console.log(this.axisYdata);
        this.onPaint()
    }
    getZeroClr(value) {
        if (value > 0.0000001) {
            return this.color.red
        } 
        else if (value < -0.0000001) {
            return this.color.green
        }
        else {
            return this.color.txt
        }
    }
    /**
     * According to the record index to obtain a set of data according to the keys, the data is {MA:[]...} is mainly provided for mouse movement
     * @param {Number} index
     * @return {Array}
     * @memberof ClChartLine
     */
    getMoveData(index) {
        let lines = this.config.lines
        const out = []
        if (!Array.isArray(lines)) return out

        let value, mess, clr
        for (let k = 0; k < lines.length; k++) {
            if (lines[k].info === undefined) continue
            if (lines[k].info.showmode === undefined) {
                lines[k].info.showmode = 'normal'
            }    
            if (lines[k].info.fieldY !== undefined) {
                if (lines[k].formula === undefined) {
                    value = getValue(this.data, lines[k].info.fieldY, index)
                } else {
                    value = getValue(this.father.getData(lines[k].formula.key), lines[k].info.fieldY,
                        index - this.axisYdata.minIndex)
                }
                mess = formatInfo(value, lines[k].info.format, this.static)
                if (lines[k].info.color === undefined) {
                    clr = getLineColor(lines[k].info.colorIndex)
                } else {
                    clr = this.color[lines[k].info.color]
                }
                if (lines[k].info.showmode === 'both' && lines[k].info.fieldX !== undefined) {
                    if (lines[k].formula === undefined) {
                        value = getValue(this.data, lines[k].info.fieldX, index)
                    } else {
                        value = getValue(this.father.getData(lines[k].formula.key), lines[k].info.fieldX,
                            index - this.axisYdata.minIndex)
                    }
                    const xinfo = formatInfo(value, lines[k].info.formatX, this.static)
                    
                    out.push({
                        index: k,
                        color: clr,
                        label: lines[k].info.label,
                        value: mess,
                        other: { value : xinfo, color: this.getZeroClr(value) }
                    })
                }
                else {
                    out.push({
                        index: k,
                        color: clr,
                        label: lines[k].info.label,
                        value: mess 
                    })
                }
            } else {
                out.push({
                    index: k,
                    color: clr,
                    label: lines[k].info.label
                })
            }
        }
        // console.log(index, out);
        return out
    }
    /**
     * draw title info
     * @param {Number} index title index
     * @memberof ClChartLine
     */
    drawTitleInfo(index) {
        if (this.config.title.display === 'none') return
        if (index === undefined || index < 0 || index > this.axisYdata.maxIndex) index = this.axisYdata.maxIndex
        this.childDraws['TITLE'].onPaint(this.getMoveData(index))
    }
    /**
     * draw child
     * @param {String} name child draw's name
     * @memberof ClChartLine
     */
    drawChildDraw(name) {
        if (this.childDraws[name] !== undefined) {
            if (name === 'TITLE') {
                this.drawTitleInfo(this.axisYdata.moveIndex)
            } else {
                this.childDraws[name].onPaint()
            }
        }
    }
    /**
     * draw all the layers contained in this area
     * @memberof ClChartLine
     */
    onPaint() {

        // 定位基本参数 计算画图相关信息 确定显示哪些数据
        this.locationData()

        this.father.readyData(this.data, this.config.lines)

        _setLineWidth(this.context, this.scale)
        this.drawClear() // 清理画图区
        
        // _drawRect(this.context, this.rectChart.left, this.rectChart.top, this.rectChart.width, this.rectChart.height)

        this.drawChildDraw('GRID') // 先画线格

        // 为画图做准备工作
        this.readyDraw()
        this.drawChildDraw('AXISX')
        this.drawChildDraw('AXISY_LEFT')
        this.drawChildDraw('AXISY_RIGHT')

        this.drawChildDraw('TITLE')
        this.drawChildLines() // 画出所有的线

        this.drawChildCharts()

    }

    // 画图前的准备工作
    /**
     * calc max and min data
     * @param {Array} data
     * @param {Object} extremum
     * @param {Number} start
     * @param {Number} stop
     * @return {Object}
     * @memberof ClChartLine
     */
    // calcMaxMin(data, extremum, start, stop) {
    //     const mm = {
    //         max: 0.0,
    //         min: 0.0,
    //         mid: 0.0
    //     }
    //     if (extremum.method === 'fixedLeft' || extremum.method === 'fixedRight') {
    //         const middle = this.getMiddle(extremum.method)
    //         mm.min = middle * (1 - 0.01)
    //         mm.max = middle * (1 + 0.01)
    //     }
    //     if (data === undefined || isEmptyArray(data.value)) return mm

    //     let value
    //     if (start === undefined) start = 0
    //     if (stop === undefined) stop = data.value.length - 1

    //     for (let k = start; k <= stop; k++) {
    //         if (!isEmptyArray(extremum.maxvalue)) {
    //             for (let m = 0; m < extremum.maxvalue.length; m++) {
    //                 if (typeof (extremum.maxvalue[m]) !== 'string') continue
    //                 value = getValue(data, extremum.maxvalue[m], k)
    //                 if (value > mm.max) {
    //                     mm.max = value
    //                 }
    //             }
    //         }
    //         if (!isEmptyArray(extremum.minvalue)) {
    //             for (let m = 0; m < extremum.minvalue.length; m++) {
    //                 if (typeof (extremum.minvalue[m]) !== 'string') continue
    //                 value = getValue(data, extremum.minvalue[m], k)
    //                 if (mm.min === 0.0) mm.min = value
    //                 if (value < mm.min) {
    //                     mm.min = value
    //                 }
    //             }
    //         }
    //         if (!isEmptyArray(extremum.midvalue)) {
    //             for (let m = 0; m < extremum.midvalue.length; m++) {
    //                 if (typeof (extremum.midvalue[m]) !== 'string') continue
    //                 value = getValue(data, extremum.midvalue[m], k)
    //                 if (value > mm.mid) {
    //                     mm.mid = value
    //                 }
    //             }
    //         }
    //     }

    //     if (!isEmptyArray(extremum.maxvalue)) {
    //         for (let m = 0; m < extremum.maxvalue.length; m++) {
    //             if (typeof (extremum.maxvalue[m]) === 'number') {
    //                 mm.max = extremum.maxvalue[m]
    //                 break
    //             }
    //         }
    //     }
    //     if (!isEmptyArray(extremum.minvalue)) {
    //         for (let m = 0; m < extremum.minvalue.length; m++) {
    //             if (typeof (extremum.minvalue[m]) === 'number') {
    //                 mm.min = extremum.minvalue[m]
    //                 break
    //             }
    //         }
    //     }
    //     if (!isEmptyArray(extremum.midvalue)) {
    //         for (let m = 0; m < extremum.midvalue.length; m++) {
    //             if (typeof (extremum.midvalue[m]) === 'number') {
    //                 mm.mid = extremum.midvalue[m]
    //                 break
    //             }
    //         }
    //     }

    //     if (extremum.method === 'fixedLeft' || extremum.method === 'fixedRight') {
    //         const middle = this.getMiddle(extremum.method)
    //         if (mm.max === mm.min) {
    //             if (mm.max > middle) mm.min = middle
    //             if (mm.min < middle) mm.max = middle
    //         }
    //         const maxrate = Math.abs(mm.max - middle) / middle
    //         const minrate = Math.abs(middle - mm.min) / middle
    //         if (maxrate < 0.01 && minrate < 0.01 &&
    //             this.static.style !== STOCK_TYPE_IDX) {
    //             mm.min = middle * (1 - 0.01)
    //             mm.max = middle * (1 + 0.01)
    //         } else {
    //             if (maxrate > minrate) {
    //                 // mm.min = middle / (1 + maxrate)
    //                 mm.min = middle * (1 - maxrate)
    //             } else {
    //                 mm.max = middle * (1 + minrate)
    //             }
    //         }
    //         if (mm.min < 0) mm.min = 0
    //     }

    //     return mm
    // }
    /**
     * location data
     * @memberof ClChartLine
     */
    // this.axisYdata.max =
    // this.axisYdata.min =
    // this.axisYdata.unitY
    // this.axisYdata.Yhead
    // this.axisYdata.Yfoot
    locationData() {
        this.data = this.father.getData(this.hotKey)
        // console.log(this.data);
        if (this.data === undefined) return
        
        let width = this.axisYdata.Yfoot - this.axisYdata.Yhead
        // this.axisYdata.unitY 不能变
        
        let maxCount = 0
        let maxv = 0
        let minv = 0
        for (let index = 0; index < this.data.value.length; index++) {
            const val = getValue(this.data, this.config.axisY.fieldY, index)
            // console.log(val, this.axisYdata.max);
            if (val < this.axisYdata.min) {
                continue
            }
            if (val > this.axisYdata.max) {
                break;
            }
            if (maxCount === 0) {
                this.axisYdata.minIndex = index
                minv = val
            } 
            this.axisYdata.maxIndex = index
            maxv = val;
            maxCount++
        }
        this.axisYdata.maxCount = maxCount    
        width *= (maxv - minv) / (this.axisYdata.max - this.axisYdata.min) 
        // this.axisYdata.spaceY 这里的意义是指每条记录
        this.axisYdata.spaceY = this.scale
        if (this.scale * maxCount < width) {
            this.axisYdata.spaceY = (width - (maxCount * this.scale)) / maxCount + this.scale
        } 
        
    }
    /**
     * set ready for draw
     * @memberof ClChartLine
     */
    readyDraw() { // 计算最大最小值等

        // 求最大最小值
        let mm, maxmin
        // const force = true;   // 由于定义了没有在后面使用，因此注释掉
        for (let i = 0; i < this.config.lines.length; i++) {
            if (this.config.lines[i].extremum === undefined) continue
            if (isEmptyArray(this.config.lines[i].extremum.maxvalue) &&
                isEmptyArray(this.config.lines[i].extremum.minvalue) &&
                isEmptyArray(this.config.lines[i].extremum.midvalue)) continue

            // 只对第一个线和有需要的线计算最大最小值
            // const ds = this.getLineDS(this.config.lines[i]);

            const formula = this.config.lines[i].formula
            if (formula !== undefined) {
                const newdata = this.father.getData(formula.key)
                mm = this.calcMaxMin(newdata, this.config.lines[i].extremum,
                    this.axisYdata.minIndex, this.axisYdata.maxIndex)
            } else {
                mm = this.calcMaxMin(this.data, this.config.lines[i].extremum,
                    this.axisYdata.minIndex, this.axisYdata.maxIndex)
            }
            if (maxmin === undefined) maxmin = mm
            else {
                maxmin.max = maxmin.max > mm.max ? maxmin.max : mm.max
                maxmin.min = maxmin.min < mm.min ? maxmin.min : mm.min
                maxmin.mid = maxmin.mid > mm.mid ? maxmin.mid : mm.mid
            }
        } // for
        this.axisXdata.max = maxmin.max
        this.axisXdata.min = maxmin.min
        this.axisXdata.mid = maxmin.mid
        this.axisXdata.unitX = (this.rectChart.width) / (this.axisXdata.max - this.axisXdata.min) // 一个单位价位多少像素
    }
    
    /* handle mouse out
     * @param {Object} event
     * @memberof ClChartLine
     */
    onMouseOut(event) {
        this.childDraws['CURSOR'].onClear()
        if (event.board === undefined || event.board === 0) {
            this.father.eventLayer.boardEvent(this.father, 'onMouseOut', event, this)
        }
    }
    /**
     * handle mouse move
     * @param {Object} event
     * @memberof ClChartLine
     */
    onMouseMove(event) {
        if (!this.linkInfo.showCursorInfo) return
        if (!this.linkInfo.showCursorLine) return

        if (event.board === undefined || event.board === 0) {
            this.father.eventLayer.boardEvent(this.father, 'onMouseMove', event, this)
        }
        
        // 找到X坐标对应的数据索引
        const mousePos = event.mousePos

        const mouseIndex = this.getMouseMoveData(mousePos.y)
        let valueY
        let valueX = 0
        // console.log(mouseIndex);
        if (mouseIndex >= this.axisYdata.minIndex && mouseIndex <= this.axisYdata.maxIndex) {
            if (inRangeY(this.rectChart, mousePos.y)) {
                this.drawTitleInfo(mouseIndex)
            }
            valueX = this.axisXdata.max - (mousePos.x - this.rectChart.left) / this.axisXdata.unitX
            if (this.config.axisX.cursor !== undefined) {
                valueX = formatInfo(valueX, this.config.axisX.cursor.format, this.static)
            }
            this.axisYdata.moveIndex = mouseIndex
        }
        this.childDraws['CURSOR'].onPaint(mousePos, valueX, valueY)
    }
    /**
     * get mouse move data
     * @param {Number} xpos
     * @return {Number}
     * @memberof ClChartLine
     */
    getMouseMoveData(ypos) {
        // 因为数据可能不连续 需要遍历
        const mouseV = this.axisYdata.max - (ypos - this.rectChart.top) / (this.axisYdata.unitY)
        
        for (let index = this.axisYdata.minIndex; index <= this.axisYdata.maxIndex; index++) {
            const val = getValue(this.data, this.config.axisY.fieldY, index)
            // console.log('--', mouseV, val, this.axisYdata.maxIndex);
            if (index == this.axisYdata.minIndex && mouseV < val) {
                return -1
            }
            if (mouseV < val) {
                return index
            }
        }
        // valueY = this.axisYdata.max - (mousePos.y - this.rectChart.top) / this.axisYdata.unitY
        return this.axisYdata.maxIndex
    }

    // /// ClChart end.
}
